import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Briefcase, Loader2 } from "lucide-react";
import { useGetIndustriesLazyQuery } from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { isLovablePreview } from "@/lib/utils/environment";
import { companies } from "@/data/dummy";
import { cn } from "@/lib/utils";
import { useCanMutate } from "@/lib/rbac";

interface EditableIndustryFieldProps {
  value: string;
  onChange: (value: string) => void;
  icon?: React.ReactNode;
}

const DUMMY_INDUSTRIES = [...new Set(
  companies.map(c => (c as any).industry).filter(Boolean).filter((v: string) => v !== "-")
)].sort() as string[];

export default function EditableIndustryField({ value, onChange, icon }: EditableIndustryFieldProps) {
  const { t } = useTranslation();
  const canMutate = useCanMutate();
  const workspaceId = useWorkspaceId();
  const [editing, setEditingState] = useState(false);
  const setEditing = (next: boolean) => {
    if (next && !canMutate) return;
    setEditingState(next);
  };
  const [draft, setDraft] = useState(value);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [getIndustries, { loading }] = useGetIndustriesLazyQuery();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Prefill only when editing STARTS. `value` is read from a ref so that a
  // mid-edit prop change (e.g. the detail sheet swapping grid-row data for the
  // slower backend result) can't clobber the user's in-progress draft.
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    if (editing) {
      setDraft(valueRef.current);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing]);

  const fetchIndustries = useCallback(async (search: string) => {
    if (isLovablePreview()) {
      const filtered = search
        ? DUMMY_INDUSTRIES.filter(i => i.toLowerCase().includes(search.toLowerCase()))
        : DUMMY_INDUSTRIES;
      setSuggestions(filtered);
      return;
    }
    if (!workspaceId) return;
    try {
      const res = await getIndustries({
        fetchPolicy: "cache-and-network",
        variables: { filter: { workspaceId: workspaceId.toString(), search } },
      });
      const items = res.data?.industries?.edges?.map(e => e?.node?.label).filter(Boolean) as string[] || [];
      setSuggestions(items);
    } catch {
      setSuggestions([]);
    }
  }, [workspaceId, getIndustries]);

  useEffect(() => {
    if (!editing || !showSuggestions) return;
    const timer = setTimeout(() => fetchIndustries(draft), 200);
    return () => clearTimeout(timer);
  }, [draft, editing, showSuggestions, fetchIndustries]);

  const commit = () => {
    const trimmed = draft.trim();
    setEditing(false);
    setShowSuggestions(false);
    if (trimmed !== value) onChange(trimmed);
  };

  const handleSelect = (item: string) => {
    setDraft(item);
    onChange(item);
    setEditing(false);
    setShowSuggestions(false);
  };

  const filtered = suggestions.filter(s =>
    !draft.trim() || s.toLowerCase().includes(draft.toLowerCase())
  );

  if (editing) {
    return (
      <div ref={containerRef} className="relative flex items-center gap-2 py-1 group">
        {icon || <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />}
        <div className="relative flex-1">
          <input
            ref={inputRef}
            value={draft}
            onChange={e => { setDraft(e.target.value); setShowSuggestions(true); }}
            onFocus={() => { setShowSuggestions(true); fetchIndustries(draft); }}
            onBlur={() => setTimeout(() => { setShowSuggestions(false); commit(); }, 150)}
            onKeyDown={e => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") { setDraft(value); setEditing(false); setShowSuggestions(false); }
            }}
            placeholder={t("grid.cell.industryPlaceholder")}
            className={cn(
              "w-full bg-transparent text-sm border-b border-accent/40 focus:border-accent outline-none py-0.5 pr-6",
            )}
          />
          {loading && <Loader2 className="absolute right-0 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />}
        </div>
        {showSuggestions && (filtered.length > 0 || (draft.trim() && loading)) && (
          <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-md border border-border bg-popover shadow-md py-1 max-h-40 overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map(s => (
                <button
                  key={s}
                  onMouseDown={e => { e.preventDefault(); handleSelect(s); }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/50 transition truncate"
                >
                  {s}
                </button>
              ))
            ) : (
              <div className="px-3 py-1.5 text-sm text-muted-foreground">{t("grid.cell.searching")}</div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!canMutate) {
    return (
      <div className="flex items-center gap-2 py-1 w-full text-left" title={t("common.readOnlyCannotChange")}>
        {icon || <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className={cn(
          "text-sm truncate",
          value ? "text-foreground" : "text-muted-foreground"
        )}>
          {value || ""}
        </span>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="flex items-center gap-2 py-1 group w-full text-left"
    >
      {icon || <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />}
      <span className={cn(
        "text-sm group-hover:text-accent transition truncate",
        value ? "text-foreground" : "text-muted-foreground"
      )}>
        {value || t("grid.cell.addIndustry")}
      </span>
    </button>
  );
}
